import { webDtos } from '@vectronic/openApi-customer';
import RTree from 'rbush';
import { L } from '../leaflet-setup/leaflet-setup';

export interface UnifiedDrawToggles {
  showMarkers: boolean;
  mapLightConditionToggle: boolean;
  mapSpeciesIconToggle: boolean;
  tracksArrows: boolean;
  showTracks: boolean;
  showMortality?: boolean;
}

export interface TrackPolyline {
  latlngs: L.LatLngExpression[];
  color: string;
  id: number; // Unique ID for caching
}

export interface CanvasMarkerData {
  position: webDtos.GPSPosition;
  id: number;
  collarName: string;
  color?: string;
  icon?: string;
  isEndpoint?: boolean;
  isNewest?: boolean;
  isOldest?: boolean;
}

export interface UnifiedData {
  standardMarkersData: CanvasMarkerData[];
  allLightConditionsData: CanvasMarkerData[];
  endpointMarkersData: CanvasMarkerData[];
  mortalityData: CanvasMarkerData[];
  speciesIconData: CanvasMarkerData[];
  trackPolylines: TrackPolyline[];
}

export interface RTreeItem {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  type: 'standard' | 'lightCondition' | 'mortality' | 'speciesIcon' | 'endpoint';
  data: CanvasMarkerData;
}

export type UnifiedCanvasClickEvent = L.LeafletMouseEvent & {
  found?: RTreeItem;
};

export type UnifiedCanvasTrackClickEvent = L.LeafletEvent & {
  latlng?: L.LatLng;
  found?: { type: 'track'; data: { id: number; color: string } };
  originalEvent?: MouseEvent;
};

type IconSizesMap = {
  speciesIcon?: [number, number];
  mortality?: [number, number];
  lightCondition?: [number, number];
  [key: string]: [number, number];
};

// --- Constants for custom arrow drawing ---
const ARROW_HEAD_BASE_X = -6;
const ARROW_HEAD_BASE_Y_OFFSET = 4; // Absolute offset from center line
const NUM_ROTATIONS = 16; // e.g., every 22.5 degrees
const ANGLE_STEP_RADIANS = (2 * Math.PI) / NUM_ROTATIONS;

type ArrowHead = {
  p1x: number;
  p1y: number;
  p2x: number;
  p2y: number;
  p3x: number;
  p3y: number;
};

const MORTALITY_SVG_TEMPLATE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <path d="M238 17c-64.1 7-118.3 44.5-146.8 101.6-25.7 51.2-25.3 110.7.9 166.3 15.2 32.2 36 60.3 87.9 118.6 35.5 39.9 59.8 69.8 70.6 87 4.5 7 6.3 7 10.9-.3 10.6-16.8 35.3-47.2 68.1-84.2 37.4-42.1 49.8-57.1 64.2-77.1 34.7-48.5 50.4-99.7 45.1-147.7-9-82.5-70.5-148.3-151.8-162.3-13.4-2.3-37-3.2-49.1-1.9z" fill="{color}"/>
    <path d="M271.4 95.6c14.1 3.6 26.2 13.2 31.9 25.1 3.2 6.8 3.2 6.9 3.5 24.9.5 24.9.7 24.2-13.9 38.8-9.1 9-12.6 13.3-14.8 18-4.3 8.9-5.9 9.6-22.3 9.6s-18.2-.8-22.7-10.4c-2.6-5.3-5.2-8.4-13.6-16.3-14.8-13.7-14.8-13.8-14.3-36.6.4-15.2.9-19.2 2.6-23.9 5.2-14.3 17.2-25.2 32.5-29.3 7.2-1.9 23.5-1.9 31.1.1z" fill="{skullFillColor}"/>
    <path d="M228.5 146.9c-6.9 1.7-10.5 5.4-10.5 10.9 0 7.5 5.6 13.2 12.9 13.2 14.4 0 22-16.4 10.3-22.4-3.7-1.8-9.1-2.5-12.7-1.7zM274.1 147c-5 1.2-9.3 5.9-9.4 10.4-.2 6.8 7.3 13.6 15.2 13.6 6.4 0 10.4-2.7 12.6-8.4 1.6-4.1 1.6-5 .4-8.3-2.3-6-10.7-9.3-18.8-7.3z" fill="{color}"/>
    <path d="M253.7 173.8c-3.5 3.8-4.9 12.6-2.5 15 .7.7 2.9 1.2 5 1.2 5.1 0 6.7-3 4.9-9.2-1.4-4.4-3.9-8.8-5.1-8.8-.4 0-1.4.8-2.3 1.8z" fill="{skullFillColor}"/>
    <path d="M190.2 187c2.5 2.6 2.9 3.7 2.6 7l-.3 3.9 31.7 16.1 31.8 16.1 31.8-16.1 31.7-16-.3-4c-.8-8.5 10-13.5 16.1-7.4 1.4 1.4 2.7 3.5 2.9 4.6.2 1.3 1.6 2.5 4.1 3.3 7 2.4 10.7 7.5 10.7 14.7 0 13.2-15.6 19.7-25.6 10.6-2.2-1.9-2.2-1.9-22.2 8.1-11 5.5-21.4 10.9-23.2 12l-3.2 1.9 27.7 14 27.7 14 3.5-2.4c2-1.3 4.4-2.4 5.5-2.4 2.8 0 7.6 2.7 8.8 4.9 1.8 3.5 1.2 9-1.3 11.5-1.9 1.9-2.1 2.8-1.4 5.6 2.7 10.4-7 20.6-17.7 18.6-7.4-1.4-13.5-9.8-12.3-17l.5-3.2-31.9-16.3-31.9-16.3-31.9 16.4-31.9 16.3.5 3.1c1.2 7.2-4.9 15.6-12.3 17-10.7 2-20.7-8.6-17.8-18.9.8-2.6.6-3.5-1.3-5.5-2.5-2.7-3.1-7.9-1.3-11.3 1.2-2.2 6-4.9 8.8-4.9 1.1 0 3.5 1.1 5.5 2.4l3.5 2.4 27.6-13.9c15.2-7.7 27.6-14.2 27.6-14.5 0-.2-10.4-5.6-23.1-12-23.1-11.5-23.1-11.5-25.3-9.6-3.4 3.1-6.2 4.2-10.8 4.2-8.2 0-14.8-6.6-14.8-14.8 0-7.2 3.7-12.3 10.7-14.7 2.5-.8 3.9-2 4.1-3.3.2-1.1 1.5-3.2 2.9-4.6 3.8-3.8 9.4-3.6 13.5.4z" fill="{skullFillColor}"/>
</svg>`;

declare module 'leaflet' {
  interface Map {
    _latLngToNewLayerPoint(latlng: LatLngExpression, zoom: number, center: LatLngExpression): Point;
  }
}

export class UnifiedMarkerCanvasLayer extends L.Layer {
  private _canvas: HTMLCanvasElement;
  private _data: UnifiedData = {
    standardMarkersData: [],
    allLightConditionsData: [],
    endpointMarkersData: [],
    mortalityData: [],
    speciesIconData: [],
    trackPolylines: [],
  };
  private _toggles: UnifiedDrawToggles = {
    showMarkers: true,
    mapLightConditionToggle: true,
    mapSpeciesIconToggle: true,
    tracksArrows: true,
    showTracks: true,
  };
  private _tree = new RTree<RTreeItem>();
  private _iconCache = new Map<string, { img: HTMLImageElement; size: [number, number] }>();
  private _iconLoadingPromises = new Map<string, Promise<HTMLImageElement>>();
  private _iconsLoaded = false;
  private _latLngsCache = new Map<number, L.LatLngExpression[]>();
  private _precomputedArrowRotations: { cos: number; sin: number }[] = [];
  private _endpointColors = { newest: '#ff0000', oldest: '#00ff00' };
  private _selectedTrackId: number | null = null;
  private _interceptClicks = true;
  private _iconSizes: IconSizesMap = {};
  private _redrawRequest: number;

  constructor(options: L.LayerOptions = { pane: 'unifiedCanvasPane' }) {
    super(options);
    L.Util.setOptions(this, options);

    this._onClick = this._onClick.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseOut = this._onMouseOut.bind(this);

    // Precompute rotations for arrow heads
    for (let i = 0; i < NUM_ROTATIONS; i++) {
      const angle = i * ANGLE_STEP_RADIANS;
      this._precomputedArrowRotations.push({
        cos: Math.cos(angle),
        sin: Math.sin(angle),
      });
    }
  }

  override onAdd(map: L.Map) {
    this._map = map;
    // Create the single canvas element
    this._canvas = L.DomUtil.create('canvas', 'leaflet-zoom-animated');
    this.getPane()?.appendChild(this._canvas);

    // Add necessary event listeners
    map.on('zoomstart', this._hideCanvas, this);
    map.on('moveend', this._reset, this);
    map.on('zoomanim', this._animateZoom, this);
    map.on('zoomend', this._reset, this);
    map.on('click', this._onClick, this);
    map.on('mousemove', this._onMouseMove, this);
    map.on('mouseout', this._onMouseOut, this);

    this._reset();
    return this;
  }

  override onRemove(map: L.Map) {
    L.DomUtil.remove(this._canvas);
    map.off('zoomstart', this._hideCanvas, this);
    map.off('zoomanim', this._animateZoom, this);
    map.off('moveend', this._reset, this);
    map.off('zoomend', this._reset, this);
    map.off('click', this._onClick, this);
    map.off('mousemove', this._onMouseMove, this);
    map.off('mouseout', this._onMouseOut, this);
    return this;
  }

  /**
   * Loads initial data and rebuilds the RTree.
   * @param data All position data.
   * @param toggles Current drawing preferences (which markers are visible).
   * @param iconSizes Size for mortality/species icons.
   */
  setData(
    data: UnifiedData,
    toggles: UnifiedDrawToggles,
    iconSizes: IconSizesMap,
    endpointColors: { newest: string; oldest: string },
  ) {
    this._toggles = toggles;
    this._data = data || {
      standardMarkersData: [],
      allLightConditionsData: [],
      endpointMarkersData: [],
      mortalityData: [],
      speciesIconData: [],
      trackPolylines: [],
    };

    this._tree.clear();
    this._latLngsCache.clear();
    this._iconSizes = iconSizes;
    this._endpointColors = endpointColors;

    const rtreeItems: RTreeItem[] = [];
    // Map the data keys to the expected RTreeItem types
    const pointDataMapping: { [key in keyof UnifiedData]?: RTreeItem['type'] } = {
      standardMarkersData: 'standard',
      allLightConditionsData: 'lightCondition',
      endpointMarkersData: 'endpoint',
      mortalityData: 'mortality',
      speciesIconData: 'speciesIcon',
    };

    // Iterate over the entries to maintain type safety with a clear assertion.
    (Object.entries(pointDataMapping) as [keyof UnifiedData, RTreeItem['type']][]).forEach(
      ([key, type]) => {
        const items = this._data[key];
        if (items && key !== 'trackPolylines') {
          items.forEach((item) => {
            // Skip items that are missing position data
            if (item.position?.latitude == null || item.position?.longitude == null) {
              return;
            }

            rtreeItems.push({
              minX: item.position.longitude,
              minY: item.position.latitude,
              maxX: item.position.longitude,
              maxY: item.position.latitude,
              type,
              data: item,
            });
          });
        }
      },
    );

    this._tree.load(rtreeItems);

    // Pre-load necessary icons
    const iconPromises: Promise<HTMLImageElement | void>[] = [];

    if (this._data.speciesIconData?.length) {
      const size = this._iconSizes.speciesIcon;
      this._data.speciesIconData.forEach((d) =>
        iconPromises.push(this._loadIcon(d.icon, d.color, size, 'speciesIcon', d)),
      );
    }
    if (this._data.mortalityData?.length) {
      const size = this._iconSizes.mortality;
      this._data.mortalityData.forEach((d) => {
        iconPromises.push(this._loadIcon(MORTALITY_SVG_TEMPLATE, d.color, size, 'mortality', d));
      });
    }
    if (this._data.allLightConditionsData?.length) {
      this._data.allLightConditionsData.forEach((d) =>
        iconPromises.push(
          this._loadIcon(d.icon, null, this._getIconSize('lightCondition', d), 'lightCondition', d),
        ),
      );
    }

    Promise.all(iconPromises)
      .then(() => {
        this._requestRedraw();
      })
      .catch((error) => {
        console.error('Failed to load one or more icons:', error);
        this._requestRedraw();
      })
      .finally(() => {
        this._iconsLoaded = true;
        this._requestRedraw();
      });
  }

  /**
   * Updates display options and forces a redraw.
   * @param toggles Current drawing preferences (which markers are visible).
   * @param iconSizes Size for mortality/species icons.
   */
  redraw(
    toggles: UnifiedDrawToggles,
    iconSizes: IconSizesMap,
    endpointColors?: { newest: string; oldest: string },
  ) {
    this._toggles = toggles;
    this._iconSizes = iconSizes;
    if (endpointColors) this._endpointColors = endpointColors;
    // Re-load icons, especially if size changed, and wait for them before redrawing.
    const iconPromises: Promise<HTMLImageElement | void>[] = [];

    if (this._data.speciesIconData?.length) {
      const size = this._iconSizes.speciesIcon;
      this._data.speciesIconData.forEach((d) =>
        iconPromises.push(this._loadIcon(d.icon, d.color, size, 'speciesIcon', d)),
      );
    }
    if (this._data.mortalityData?.length) {
      const size = this._iconSizes.mortality;
      this._data.mortalityData.forEach((d) => {
        iconPromises.push(this._loadIcon(MORTALITY_SVG_TEMPLATE, d.color, size, 'mortality', d));
      });
    }
    if (this._data.allLightConditionsData?.length) {
      this._data.allLightConditionsData.forEach((d) => {
        const size = this._getIconSize('lightCondition', d);
        iconPromises.push(this._loadIcon(d.icon, null, size, 'lightCondition', d));
      });
    }

    // IMPORTANT: Wait for all promises to resolve before redrawing.
    Promise.all(iconPromises).then(() => {
      this._requestRedraw();
    });
  }

  /**
   * Checks for a feature hit at the given LatLng.
   * Hit detection respects the current draw toggles.
   */
  private _getNearby(latlng: L.LatLng, tolerance = 10) {
    if (!this._map || !this._tree) return null;

    const clickPoint = this._map.latLngToContainerPoint(latlng);
    const searchBounds = this._buildSearchBounds(clickPoint);

    const candidates = this._tree.search(searchBounds);
    const foundItems: RTreeItem[] = [];

    for (const item of candidates) {
      // 1. Filter out items that are currently toggled OFF
      if (this._isItemToggledOff(item)) continue;

      // 2. Check for pixel-perfect hit
      const posPoint = this._map.latLngToContainerPoint(
        L.latLng(item.data.position.latitude, item.data.position.longitude),
      );

      if (this._hitTestItem(item, clickPoint, posPoint, tolerance)) {
        foundItems.push(item);
      }
    }

    if (foundItems.length === 0) return null;

    // Prioritize what to return if multiple items are found (Mortality is generally most important)
    const priority = ['mortality', 'speciesIcon', 'lightCondition', 'endpoint', 'standard'];
    foundItems.sort((a, b) => priority.indexOf(a.type) - priority.indexOf(b.type));

    return foundItems[0];
  }

  private _buildSearchBounds(clickPoint: L.Point) {
    // Define a pixel-based search area (e.g., 40x40 pixels)
    const searchBoxSize = 40;
    const p1 = L.point(clickPoint.x - searchBoxSize / 2, clickPoint.y - searchBoxSize / 2);
    const p2 = L.point(clickPoint.x + searchBoxSize / 2, clickPoint.y + searchBoxSize / 2);
    const searchBoundsLatLng = L.latLngBounds(
      this._map.containerPointToLatLng(p1),
      this._map.containerPointToLatLng(p2),
    );
    return {
      minX: searchBoundsLatLng.getWest(),
      minY: searchBoundsLatLng.getSouth(),
      maxX: searchBoundsLatLng.getEast(),
      maxY: searchBoundsLatLng.getNorth(),
    };
  }

  private _isItemToggledOff(item: RTreeItem): boolean {
    if ((item.type === 'standard' || item.type === 'endpoint') && !this._toggles.showMarkers)
      return true;
    if (item.type === 'lightCondition' && !this._toggles.mapLightConditionToggle) return true;
    if (item.type === 'speciesIcon' && !this._toggles.mapSpeciesIconToggle) return true;
    return false;
  }

  private _hitTestItem(
    item: RTreeItem,
    clickPoint: L.Point,
    posPoint: L.Point,
    tolerance: number,
  ): boolean {
    const distance = clickPoint.distanceTo(posPoint);
    if (item.type === 'standard' || item.type === 'endpoint') {
      // Standard markers are circles
      return distance < tolerance;
    }

    // lightCondition, mortality, speciesIcon are images/icons
    const iconSize = this._getIconSize(item.type, item.data) || [40, 40];
    const [iconWidth, iconHeight] = iconSize;
    const halfWidth = iconWidth / 2;
    const halfHeight = iconHeight / 2;

    // Check if the click is inside the icon's bounding box
    return (
      clickPoint.x >= posPoint.x - halfWidth &&
      clickPoint.x <= posPoint.x + halfWidth &&
      clickPoint.y >= posPoint.y - halfHeight &&
      clickPoint.y <= posPoint.y + halfHeight
    );
  }

  private _getNearbyTrack(latlng: L.LatLng, tolerance = 8) {
    if (!this._map || !this._toggles.showTracks) return null;

    const clickPoint = this._map.latLngToLayerPoint(latlng);
    let closestTrack: { type: 'track'; data: { id: number; color: string } } | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    (this._data.trackPolylines || []).forEach((track: TrackPolyline) => {
      if (!track.latlngs || track.latlngs.length < 2) return;

      const points = track.latlngs.map((trackLatLng) =>
        this._map.latLngToLayerPoint(this._toLatLng(trackLatLng)),
      );

      for (let i = 0; i < points.length - 1; i++) {
        const distance = this._distanceToSegment(clickPoint, points[i], points[i + 1]);
        if (distance <= tolerance && distance < closestDistance) {
          closestDistance = distance;
          closestTrack = {
            type: 'track',
            data: { id: track.id, color: track.color },
          };
        }
      }
    });

    return closestTrack;
  }

  private _distanceToSegment(point: L.Point, start: L.Point, end: L.Point) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;

    if (dx === 0 && dy === 0) {
      return point.distanceTo(start);
    }

    const t = Math.max(
      0,
      Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)),
    );
    const projectedPoint = L.point(start.x + t * dx, start.y + t * dy);
    return point.distanceTo(projectedPoint);
  }

  private _toLatLng(latlng: L.LatLngExpression) {
    if (Array.isArray(latlng)) {
      return L.latLng(latlng);
    }
    if ('lat' in latlng && 'lng' in latlng) {
      return latlng as L.LatLng;
    }
    return L.latLng(latlng);
  }

  setSelectedTrack(trackId: number | null) {
    this._selectedTrackId = trackId;
    this._requestRedraw();
  }

  /**
   * Suspends or resumes this layer's click hit-testing. The pane's canvas
   * paints the collar markers/tracks above the interactive vector layers (ESRI
   * feature paths, virtual-fence polygons) and stays DOM-transparent, so those
   * layers receive their clicks natively; this flag only decides whether
   * clicks that bubble to the map are additionally treated as collar/track
   * hits. Drawing tools (distance measurement, virtual-fence editing) suspend
   * it so their vertex clicks are not eaten — see MapService, which wires this
   * to DrawingManagerService.activeDrawingMode.
   */
  setInterceptClicks(enabled: boolean) {
    this._interceptClicks = enabled;
  }

  /**
   * Whether a collar marker or track sits within click tolerance of the given
   * position (respecting the draw toggles, like the click handling). MapService
   * checks this on preclick so a click on a marker/track keeps the selection
   * for the layer's own select/toggle handling instead of clearing it.
   */
  hasHitAt(latlng?: L.LatLng) {
    if (!this._interceptClicks || !latlng) return false;
    return !!(this._getNearby(latlng) || this._getNearbyTrack(latlng));
  }

  /**
   * Resolves a click at the given position against the drawn markers/tracks
   * and fires 'click'/'track-click' on a hit. Used by the map-level click
   * handler and by feature layers handing a click over when a marker/track
   * sits on top of them (see EsriMapLayersService's collision handler).
   */
  resolveClick(latlng: L.LatLng, sourceEvent?: L.LeafletMouseEvent) {
    const found = this._getNearby(latlng);
    if (found) {
      this.fire('click', { ...(sourceEvent ?? {}), latlng, found });
      return true;
    }

    const foundTrack = this._getNearbyTrack(latlng);
    if (foundTrack) {
      this.fire('track-click', { ...(sourceEvent ?? {}), latlng, found: foundTrack });
      return true;
    }

    return false;
  }

  private _onClick(e: L.LeafletMouseEvent) {
    if (!this._map) return;

    // Suspended while another tool consumes the raw map clicks.
    if (!this._interceptClicks) {
      return;
    }

    if (this.resolveClick(e.latlng, e)) {
      // Stop propagation to prevent the map itself from handling the click
      L.DomEvent.stop(e);
    }
  }

  private _onMouseMove(e: L.LeafletMouseEvent) {
    if (!this._map) return;
    const found = this._getNearby(e.latlng);
    this._map.getContainer().style.cursor = found ? 'pointer' : '';
  }

  private _onMouseOut() {
    if (!this._map) return;
    this._map.getContainer().style.cursor = '';
  }

  private _reset() {
    if (!this._map || !this._canvas) return;

    // Safety check: if map has no size, don't try to position canvas yet
    const size = this._map.getSize();
    if (size.x === 0 || size.y === 0) return;

    this._canvas.style.visibility = 'visible';
    const bounds = this._map.getBounds();
    const topLeft = this._map.latLngToLayerPoint(bounds.getNorthWest());
    L.DomUtil.setPosition(this._canvas, topLeft);

    this._canvas.width = size.x;
    this._canvas.height = size.y;
    this._requestRedraw();
  }

  private _animateZoom(e: L.ZoomAnimEvent) {
    if (!this._map) return;
    const scale = this._map.getZoomScale(e.zoom, this._map.getZoom());
    const offset = this._map
      ._latLngToNewLayerPoint(this._map.getBounds().getNorthWest(), e.zoom, e.center)
      .round();
    L.DomUtil.setTransform(this._canvas, offset, scale);
  }

  private _hideCanvas() {
    if (this._canvas) this._canvas.style.visibility = 'hidden';
  }

  private _requestRedraw() {
    if (this._redrawRequest) L.Util.cancelAnimFrame(this._redrawRequest);
    this._redrawRequest = L.Util.requestAnimFrame(this._redraw, this);
  }

  private _redraw() {
    if (!this._map || !this._canvas || !this._iconsLoaded) {
      return;
    }
    const currentZoom = this._map.getZoom();
    const pixelBounds = this._map.getPixelBounds();
    if (!pixelBounds.min || !pixelBounds.max) {
      return;
    }

    const ctx = this._canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);

    const canvasOrigin = L.DomUtil.getPosition(this._canvas);
    const bounds = this._map.getBounds();

    const visibleItems = this._tree.search({
      minX: bounds.getWest(),
      minY: bounds.getSouth(),
      maxX: bounds.getEast(),
      maxY: bounds.getNorth(),
    });

    // --- Tracks Arrows Logic ---
    // Draw polylines first, then arrows on top.
    if (this._toggles.showTracks) {
      this._drawTrackPolylines(ctx, canvasOrigin);
      this._drawTrackArrows(ctx, canvasOrigin);
    }

    // --- Canvas Drawing Logic ---

    // Draw in order of standard (lowest) to mortality (highest)
    const drawOrder: RTreeItem['type'][] = [
      'standard', // Non-endpoint markers
      'lightCondition',
      'endpoint', // New draw pass for endpoint markers
      'mortality',
      'speciesIcon',
    ];

    drawOrder.forEach((type) => {
      // === TOGGLE CHECK: Only draw if the corresponding toggle is ON ===
      if (type === 'lightCondition' && !this._toggles.mapLightConditionToggle) {
        return;
      } else if (type === 'speciesIcon' && !this._toggles.mapSpeciesIconToggle) {
        return;
      } else if (type === 'mortality') {
        // Logic: Use the explicit showMortality flag if it exists.
        // If undefined, fallback to the old logic (markers OR lightCondition).
        const shouldDraw =
          this._toggles.showMortality ??
          (this._toggles.showMarkers || this._toggles.mapLightConditionToggle);

        if (!shouldDraw) {
          return;
        }
      }
      // For endpoints, we also check the main 'showMarkers' toggle.
      else if (type === 'standard' && !this._toggles.showMarkers) {
        return;
      }

      let itemsToDraw = visibleItems.filter((item) => {
        return item.type === type;
      });

      // --- Dynamic Thinning for Light Conditions based on Zoom ---
      if (type === 'lightCondition') {
        itemsToDraw = this._dynamicThinning(itemsToDraw, this._map, currentZoom);
      }

      this._drawItemsOfType(ctx, type, itemsToDraw, canvasOrigin);
    });
  }

  private _drawItemsOfType(
    ctx: CanvasRenderingContext2D,
    type: RTreeItem['type'],
    itemsToDraw: RTreeItem[],
    canvasOrigin: L.Point,
  ) {
    itemsToDraw.forEach((item) => {
      const p = this._map
        .latLngToLayerPoint(L.latLng(item.data.position.latitude, item.data.position.longitude))
        .subtract(canvasOrigin);
      this._drawItem(ctx, type, item, p);
    });
  }

  private _drawItem(
    ctx: CanvasRenderingContext2D,
    type: RTreeItem['type'],
    item: RTreeItem,
    p: L.Point,
  ) {
    if (type === 'standard') {
      this._drawStandardItem(ctx, item, p);
    } else if (type === 'endpoint') {
      this._drawEndpointItem(ctx, item, p);
    } else {
      this._drawIconItem(ctx, item, p);
    }
  }

  private _drawStandardItem(ctx: CanvasRenderingContext2D, item: RTreeItem, p: L.Point) {
    // If the marker is an endpoint, it's handled by the 'endpoint' type now.
    if (item.data.isEndpoint) {
      return;
    }
    // Draw standard circle
    ctx.beginPath();
    ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
    ctx.fillStyle = item.data.color;
    ctx.fill();
  }

  private _drawEndpointItem(ctx: CanvasRenderingContext2D, item: RTreeItem, p: L.Point) {
    // Logic to hide endpoint marker if a species icon is shown at the same position
    if (this._toggles.mapSpeciesIconToggle) {
      const hasSpeciesIcon = this._data.speciesIconData.some(
        (d) => d.position === item.data.position,
      );
      if (hasSpeciesIcon) return; // Skip drawing this endpoint
    }

    const endpointColor = item.data.isNewest
      ? this._endpointColors.newest
      : this._endpointColors.oldest;
    // Draw endpoint circle with a border
    ctx.beginPath();
    ctx.arc(p.x, p.y, 6, 0, Math.PI * 2); // radius 6
    ctx.strokeStyle = endpointColor;
    ctx.lineWidth = 3;
    ctx.stroke();
  }

  private _drawIconItem(ctx: CanvasRenderingContext2D, item: RTreeItem, p: L.Point) {
    // If the icon is at an endpoint, skip drawing it.
    if (item.data.isEndpoint && item.type === 'lightCondition') {
      return;
    }
    const size = this._getIconSize(item.type, item.data);
    const cacheKey = this._getCacheKey(item.type, item.data, size);
    if (!cacheKey) return;

    const iconData = this._iconCache.get(cacheKey);
    if (iconData) {
      ctx.drawImage(
        iconData.img,
        p.x - iconData.size[0] / 2, // Centered X
        p.y - iconData.size[1] / 2, // Centered Y
        iconData.size[0],
        iconData.size[1],
      );
    } else {
      // This is a critical warning. If you see this, icons are not being loaded/cached correctly.
      console.warn(`Icon not found in cache for type '${item.type}'. Cache key:`, cacheKey);
    }
  }

  private _dynamicThinning(items: RTreeItem[], map: L.Map, zoom: number): RTreeItem[] {
    // 1. Determine Grid Size
    let cellSize: number;
    if (zoom < 8) cellSize = 80;
    else if (zoom < 12) cellSize = 40;
    else cellSize = 15;

    const buckets = new Map<string, RTreeItem>();

    // 2. Optimization: Get bounds once to filter off-screen items
    const bounds = map.getBounds();

    for (const item of items) {
      const lat = item.data.position.latitude;
      const lng = item.data.position.longitude;

      // 3. Bounds Check: Skip items strictly outside the view
      if (!bounds.contains([lat, lng])) {
        continue;
      }

      const point = map.latLngToContainerPoint([lat, lng]);

      const cellX = Math.floor(point.x / cellSize);
      const cellY = Math.floor(point.y / cellSize);
      const key = `${cellX},${cellY}`;

      buckets.set(key, item);
    }

    return Array.from(buckets.values());
  }

  private _drawTrackPolylines(ctx: CanvasRenderingContext2D, canvasOrigin: L.Point) {
    const selectedTrackId = this._selectedTrackId;
    const trackPolylines = [...(this._data.trackPolylines || [])].sort((a, b) => {
      if (a.id === selectedTrackId) return 1;
      if (b.id === selectedTrackId) return -1;
      return 0;
    });

    trackPolylines.forEach(({ id, latlngs, color }) => {
      if (!latlngs || latlngs.length === 0) {
        return;
      }

      // Cache the latlngs for the arrow drawing function to use
      this._latLngsCache.set(id, latlngs);

      const points = latlngs.map((latlng) =>
        this._map.latLngToLayerPoint(this._toLatLng(latlng)).subtract(canvasOrigin),
      );

      const isSelected = selectedTrackId === id;
      const previousLineWidth = ctx.lineWidth;

      ctx.lineWidth = isSelected ? 2.5 : 1;
      ctx.strokeStyle = color || 'black';
      ctx.beginPath();
      // The points are now oldest-to-newest. We can draw them sequentially.
      if (points.length > 0) {
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
      }
      ctx.stroke();
      ctx.lineWidth = previousLineWidth;
    });
  }

  private _drawTrackArrows(ctx: CanvasRenderingContext2D, canvasOrigin: L.Point) {
    // --- Track Arrows Logic ---
    if (!this._toggles.tracksArrows) return; // Only draw arrows if their specific toggle is on.
    const arrowsByColor = new Map<string, ArrowHead[]>();

    const currentZoom = this._map.getZoom();
    let baseDistanceThreshold: number; // in pixels

    // Define pixel distance thresholds based on zoom level
    if (currentZoom > 16) {
      baseDistanceThreshold = 300;
    } else if (currentZoom > 12) {
      baseDistanceThreshold = 500;
    } else if (currentZoom > 8) {
      baseDistanceThreshold = 700;
    } else {
      baseDistanceThreshold = 800;
    }

    const visiblePolylines = this._data.trackPolylines || [];

    let totalVisiblePositions = 0;
    visiblePolylines.forEach(({ latlngs }) => {
      totalVisiblePositions += latlngs.length;
    });

    // Dynamically adjust threshold based on the total complexity in view.
    let distanceThreshold = baseDistanceThreshold;
    if (totalVisiblePositions > 1000 && currentZoom > 15) distanceThreshold *= 5;
    if (totalVisiblePositions > 500 && currentZoom > 15) distanceThreshold *= 2;

    visiblePolylines.forEach(({ id, color }) => {
      const latlngs = this._latLngsCache.get(id);
      if (!latlngs || latlngs.length < 2) return;

      // The latlngs are newest-to-oldest. We iterate forward to get the direction
      // from a newer point (p1) to an older point (p2).
      const pointsToDraw = latlngs.map((latlng) =>
        this._map.latLngToLayerPoint(this._toLatLng(latlng)),
      );

      if (pointsToDraw.length > 30000 && currentZoom > 15) {
        return;
      }

      const arrowsDrawn = this._placePolylineArrows(
        pointsToDraw,
        color,
        distanceThreshold,
        canvasOrigin,
        arrowsByColor,
      );
      this._placeMidpointArrowIfNoneDrawn(
        pointsToDraw,
        color,
        canvasOrigin,
        currentZoom,
        arrowsByColor,
        arrowsDrawn,
      );
    });

    ctx.lineWidth = 2;
    arrowsByColor.forEach((arrows, color) => {
      ctx.strokeStyle = color || 'black';
      ctx.beginPath();
      arrows.forEach((arrow) => {
        ctx.moveTo(arrow.p1x, arrow.p1y);
        ctx.lineTo(arrow.p2x, arrow.p2y);
        ctx.lineTo(arrow.p3x, arrow.p3y);
      });
      ctx.stroke();
    });
  }

  private _placePolylineArrows(
    pointsToDraw: L.Point[],
    color: string,
    distanceThreshold: number,
    canvasOrigin: L.Point,
    arrowsByColor: Map<string, ArrowHead[]>,
  ): number {
    let distanceToNextArrow = distanceThreshold;
    let polylineArrowsDrawn = 0;

    for (let i = 0; i < pointsToDraw.length - 1; i++) {
      const p1 = pointsToDraw[i].subtract(canvasOrigin); // Newer point
      const p2 = pointsToDraw[i + 1].subtract(canvasOrigin); // Older point

      const segmentPixelLength = p1.distanceTo(p2);
      const segmentAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

      let distanceAlongSegment = 0;

      while (distanceToNextArrow <= segmentPixelLength - distanceAlongSegment) {
        distanceAlongSegment += distanceToNextArrow;

        const ratio = distanceAlongSegment / segmentPixelLength;
        const arrowPoint = L.point(p1.x + (p2.x - p1.x) * ratio, p1.y + (p2.y - p1.y) * ratio);

        this._addArrow(arrowsByColor, color, this._createArrowHead(arrowPoint, segmentAngle));
        polylineArrowsDrawn++;
        distanceToNextArrow = distanceThreshold;
      }
      distanceToNextArrow -= segmentPixelLength - distanceAlongSegment;
    }

    return polylineArrowsDrawn;
  }

  private _placeMidpointArrowIfNoneDrawn(
    pointsToDraw: L.Point[],
    color: string,
    canvasOrigin: L.Point,
    currentZoom: number,
    arrowsByColor: Map<string, ArrowHead[]>,
    arrowsDrawn: number,
  ): void {
    if (arrowsDrawn !== 0 || pointsToDraw.length <= 1 || currentZoom >= 20) {
      return;
    }

    let totalPixelLength = 0;
    for (let i = 0; i < pointsToDraw.length - 1; i++) {
      totalPixelLength += pointsToDraw[i].distanceTo(pointsToDraw[i + 1]);
    }
    const midPixelLength = totalPixelLength / 2;
    let accumulatedLength = 0;
    for (let i = 0; i < pointsToDraw.length - 1; i++) {
      const p1 = pointsToDraw[i];
      const p2 = pointsToDraw[i + 1];
      const segmentLength = p1.distanceTo(p2);
      if (accumulatedLength + segmentLength >= midPixelLength) {
        const distanceIntoSegment = midPixelLength - accumulatedLength;
        const ratio = distanceIntoSegment / segmentLength;
        const midPoint = L.point(
          p1.x + (p2.x - p1.x) * ratio,
          p1.y + (p2.y - p1.y) * ratio,
        ).subtract(canvasOrigin);
        const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
        this._addArrow(arrowsByColor, color, this._createArrowHead(midPoint, angle));
        break;
      }
      accumulatedLength += segmentLength;
    }
  }

  private _addArrow(
    arrowsByColor: Map<string, ArrowHead[]>,
    color: string,
    arrow: ArrowHead,
  ): void {
    if (!arrowsByColor.has(color)) {
      arrowsByColor.set(color, []);
    }
    arrowsByColor.get(color).push(arrow);
  }

  private _createArrowHead(point: L.Point, angle: number): ArrowHead {
    const angleIndex = Math.round(angle / ANGLE_STEP_RADIANS) % NUM_ROTATIONS;
    const { cos: cosA, sin: sinA } =
      this._precomputedArrowRotations[angleIndex < 0 ? angleIndex + NUM_ROTATIONS : angleIndex];

    return {
      p1x: point.x + (ARROW_HEAD_BASE_X * cosA - -ARROW_HEAD_BASE_Y_OFFSET * sinA),
      p1y: point.y + (ARROW_HEAD_BASE_X * sinA + -ARROW_HEAD_BASE_Y_OFFSET * cosA),
      p2x: point.x,
      p2y: point.y,
      p3x: point.x + (ARROW_HEAD_BASE_X * cosA - ARROW_HEAD_BASE_Y_OFFSET * sinA),
      p3y: point.y + (ARROW_HEAD_BASE_X * sinA + ARROW_HEAD_BASE_Y_OFFSET * cosA),
    };
  }

  private _loadIcon(
    svgString: string,
    color: string | null,
    size: [number, number],
    type: RTreeItem['type'],
    data: RTreeItem['data'],
  ) {
    const cacheKey = this._getCacheKey(type, data, size);
    if (!cacheKey) {
      return Promise.resolve();
    }

    if (this._iconCache.has(cacheKey)) {
      return Promise.resolve(this._iconCache.get(cacheKey).img);
    }

    if (this._iconLoadingPromises.has(cacheKey)) {
      return this._iconLoadingPromises.get(cacheKey);
    }
    const promise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image(size[0], size[1]);

      img.onload = () => {
        this._iconCache.set(cacheKey, { img, size });
        resolve(img);
      };
      img.onerror = (err) => {
        this._iconLoadingPromises.delete(cacheKey);
        reject(err);
      };

      let styledSvgString: string;

      // If the 'svgString' is a path (contains 'assets/'), use it directly.
      // Otherwise, process it as an inline SVG string.
      if (type === 'lightCondition') {
        if (svgString.includes('assets/')) {
          img.src = svgString;
          return;
        }
        styledSvgString = svgString; // It's a pre-colored SVG string
      } else {
        if (type === 'mortality') {
          const r = parseInt(color.substring(1, 3), 16);
          const g = parseInt(color.substring(3, 5), 16);
          const b = parseInt(color.substring(5, 7), 16);
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          const skullFillColor = luminance > 128 ? 'black' : 'white';
          styledSvgString = svgString
            .replaceAll('{color}', color)
            .replaceAll('{skullFillColor}', skullFillColor);
        } else if (type === 'speciesIcon') {
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
          svgDoc.querySelectorAll('path').forEach((path) => path.setAttribute('fill', color));
          styledSvgString = new XMLSerializer().serializeToString(svgDoc);
        }
      }
      img.src = 'data:image/svg+xml,' + encodeURIComponent(styledSvgString);
    });
    this._iconLoadingPromises.set(cacheKey, promise);

    return promise;
  }

  private _getIconSize(type: RTreeItem['type'], data: RTreeItem['data']) {
    if (type === 'lightCondition' && data?.position?.lightCondition) {
      const specificKey = `lightCondition_${data.position.lightCondition}`;
      return this._iconSizes[specificKey] || this._iconSizes.lightCondition;
      // Use the specific size if it exists (e.g., 'lightCondition_TWILIGHT'), otherwise fall back to the generic 'lightCondition' size.
    }
    // For other types, use the direct key.
    return this._iconSizes[type];
  }

  private _getCacheKey(type: RTreeItem['type'], data: RTreeItem['data'], size: [number, number]) {
    const iconIdentifier = type === 'mortality' ? MORTALITY_SVG_TEMPLATE : data.icon;
    if (!iconIdentifier) return null;

    const sizeString = size.join('x');

    switch (type) {
      case 'lightCondition':
        return `${iconIdentifier}_${sizeString}_${type}`;
      case 'speciesIcon':
      case 'mortality':
        return `${data.color}_${iconIdentifier}_${sizeString}_${type}`;
      default:
        return null;
    }
  }
}
